import { useEffect, useState } from 'react';
import { useFormik } from 'formik';
import { useMutation } from '@tanstack/react-query';
import { FormikForm } from '@/components/form/FormikForm/FormikForm';
import { FormikInput } from '@/components/form/fields/FormikInput/FormikInput';
import { FormikPhoneInput } from '@/components/form/fields/FormikPhoneInput/FormikPhoneInput';
import { FormikSelectableTabs } from '@/components/form/fields/FormikSelectableTabs/FormikSelectableTabs';
import { FormikRadioGroup } from '@/components/form/fields/FormikRadioGroup/FormikRadioGroup';
import { FormikSwitchToggle } from '@/components/form/fields/FormikSwitchToggle/FormikSwitchToggle';
import { SwitchToggle } from '@/components/form/fields/SwitchToggle/SwitchToggle';
import { Box } from '@/components/Box/Box';
import { Typography } from '@/components/Typography/Typography';
import { copyToClipboard, showSnackbarResponseError } from '@/helpers/helpers';
import { amplitudeService } from '@/services/amplitudeService/amplitudeService';
import { AMPLITUDE_EVENTS } from '@/services/amplitudeService/amplitudeEvents';
import { MyVaultCatalogType, SecureLinkType, VaultAccountType } from '@/types/myVaultTypes';
import { CreateSecureLinkPayloadType } from '@/api/v1/organization/my-vault/createSecureLinkApi';
import { createSecureLink } from '../../api/api';
import { SECURE_LINK_EXPIRY_LABEL, SECURE_LINK_VIA_LABEL } from '../../const/const';
import { SHARE_INITIAL_VALUES, ShareFormType, shareValidationSchema } from './const';
import { Dialog } from '@/components-new/Dialog/Dialog';
import { ButtonNew } from '@/components-new/ButtonNew/ButtonNew';
import { COLORS } from '@/styles/colors';
import { IconButton } from '@/components/IconButton/IconButton';
import { FaCopy } from 'react-icons/fa';
import { Tooltip } from '@/components/Tooltip/Tooltip';
import { HighlightPanel } from '@/components-new/HighlightPanel/styles';
import { Mono } from '@/styles/common';

type ShareSecureLinkModalType = {
  isOpen: boolean;
  catalog: MyVaultCatalogType;
  account: VaultAccountType;
  initialRail?: string;
  onClose: () => void;
  onSuccess: () => void;
};

export const ShareSecureLinkModal = ({
  isOpen,
  catalog,
  account,
  initialRail,
  onClose,
  onSuccess,
}: ShareSecureLinkModalType) => {
  const [sent, setSent] = useState<SecureLinkType | null>(null);
  const meta = catalog.regions[account.region];
  const railLabel = (code: string) => meta.rails.find((rail) => rail.code === code)?.label ?? code;

  const { mutate, data, isPending } = useMutation({
    mutationKey: ['create_secure_link'],
    mutationFn: (payload: CreateSecureLinkPayloadType) => createSecureLink(payload),
    onSuccess(response) {
      if (response?.error) return showSnackbarResponseError(response.error);

      amplitudeService.logEvent(AMPLITUDE_EVENTS.ShareSecureLinkSuccess);
      setSent(response.body);
      onSuccess();
    },
  });

  const formik = useFormik<ShareFormType>({
    initialValues: SHARE_INITIAL_VALUES(initialRail ?? account.rails[0]),
    validationSchema: shareValidationSchema,
    onSubmit: (values) => {
      mutate({ ...values, vault_account_id: account.id, sent_to: values.sent_to.trim() });
    },
  });

  const isVerify = formik.values.mode === 'verify';
  const needsAddress = formik.values.via === 'email' || formik.values.via === 'sms';

  useEffect(() => {
    formik.setFieldValue('sent_to', '');
    formik.setFieldTouched('sent_to', false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formik.values.via]);

  const copy = async () => {
    copyToClipboard(sent.url, 'Link successfully copied to clipboard');
  };

  const sharedLabel = SECURE_LINK_EXPIRY_LABEL[formik.values.expiry_code];

  return (
    <Dialog isOpen={isOpen} header='Share banking instructions' width='760px' onClose={onClose}>
      <Typography variant='bodySmall' marginBottom='12px'>
        {account.nickname} · {account.bank_name} {account.masked_number} · {account.vault}
      </Typography>

      {sent ? (
        <Box display='flex' flexDirection='column' rowGap='14px'>
          <Typography variant='body' fontWeight='semibold'>
            {needsAddress ? 'Link sent' : `Link ready to paste into ${SECURE_LINK_VIA_LABEL[sent.via]}`}
          </Typography>
          <Box
            display='flex'
            alignItems='center'
            justifyContent='space-between'
            columnGap='12px'
            padding={'16px'}
            background={COLORS.grey[150]}
            border={`1px solid ${COLORS.grey[400]}`}
            borderRadius={'12px'}
          >
            <Mono>{sent.url}</Mono>
            <Box display='flex' flexShrink={0} gap={'8px'} alignItems='center'>
              <Typography
                background={COLORS.grey[350]}
                variant='caption'
                padding={'4px 6px'}
                flexShrink={0}
                fontWeight='semibold'
                borderRadius={'6px'}
              >
                Share · {sharedLabel}
              </Typography>
              <IconButton data-tooltip-id={'copy-link'} onClick={copy}>
                <FaCopy />
                <Tooltip id='copy-link'>Copy link</Tooltip>
              </IconButton>
            </Box>
          </Box>
          <Typography variant='bodySmall'>
            {sent.mode === 'verify'
              ? `The recipient verifies with a government ID and a liveness check before the ${sent.rail_label} instructions appear. Each open is logged with the verified viewer and you can revoke the link from Secure links.`
              : `No verification on this link. Anyone who opens it sees the ${sent.rail_label} instructions. Opens are logged by time and device only, and you can revoke the link from Secure links.`}
          </Typography>
          <Box display='flex' justifyContent='flex-end' marginTop={'12px'}>
            <ButtonNew size='medium' onClick={onClose}>
              Done
            </ButtonNew>
          </Box>
        </Box>
      ) : (
        <FormikForm value={formik}>
          <FormikSelectableTabs
            name='mode'
            direction='row'
            label={<b>How should the recipient get access?</b>}
            options={[
              {
                value: 'share',
                label: 'Share',
                description: 'No verification. Anyone with the link can view once it is opened.',
              },
              {
                value: 'verify',
                label: 'Verify and share',
                description: 'Government ID and liveness check first. You see who viewed.',
              },
            ]}
          />
          <br />

          <FormikRadioGroup
            name='rail'
            label={<b>Which instructions do you want to share?</b>}
            options={account.rails.map((code) => ({ value: code, label: `Share ${railLabel(code)}` }))}
          />
          <br />

          <FormikRadioGroup
            name='via'
            label={<b>Send the secure link by</b>}
            options={Object.entries(SECURE_LINK_VIA_LABEL).map(([value, label]) => ({ value, label }))}
          />
          <br />

          {formik.values.via === 'email' && (
            <FormikInput
              name='sent_to'
              label='Email address*'
              placeholder='name@company.com'
              autoComplete='new-field'
              apiError={data?.error}
            />
          )}
          {formik.values.via === 'sms' && (
            <FormikPhoneInput name='sent_to' label='Mobile number*' apiError={data?.error} />
          )}
          {!needsAddress && (
            <FormikInput
              name='sent_to'
              label='Recipient name (for your log)'
              placeholder='Who gets this link'
              autoComplete='new-field'
            />
          )}

          <Box>
            <FormikSelectableTabs
              name='audience'
              label='Who receives this link'
              direction='row'
              options={[
                {
                  value: 'recipient',
                  label: 'Recipient',
                  description: 'The person who will send you the payment views it themselves.',
                },
                {
                  value: 'proxy',
                  label: 'Proxy',
                  description: 'An assistant or bookkeeper who can forward the link or view it on their behalf.',
                },
              ]}
            />

            <HighlightPanel margin='30px 0 0 '>
              <Box display='flex' gap='16px' flexDirection='column'>
                <Typography fontWeight='semibold'>Link controls</Typography>
                <SwitchToggle
                  name='verification_required'
                  label='ID verification required'
                  hint={
                    <Typography variant='bodySmall' color={COLORS.grey[650]}>
                      Government ID and liveness check through TrustStamp before the details open.
                    </Typography>
                  }
                  checked={isVerify}
                  hasErrorComponent={false}
                  onChange={(event) => formik.setFieldValue('mode', event.currentTarget.checked ? 'verify' : 'share')}
                />
                <FormikRadioGroup
                  name='expiry_code'
                  label='Expires after'
                  options={Object.entries(SECURE_LINK_EXPIRY_LABEL).map(([value, label]) => ({ value, label }))}
                />
                <FormikSwitchToggle
                  name='one_time'
                  label='One-time view'
                  hasErrorComponent={false}
                  hint={
                    <Typography variant='bodySmall' color={COLORS.grey[650]}>
                      The link closes after the first verified view.
                    </Typography>
                  }
                />
                {isVerify && (
                  <FormikSwitchToggle
                    name='named_only'
                    label='Named recipient only'
                    hasErrorComponent={false}
                    hint={
                      <Typography variant='bodySmall' color={COLORS.grey[650]}>
                        Only a person who verifies with this email or number can open it.
                      </Typography>
                    }
                  />
                )}
              </Box>
            </HighlightPanel>
          </Box>

          <Box display='flex' justifyContent='flex-end' columnGap='12px' marginTop='24px'>
            <ButtonNew onClick={() => formik.handleSubmit()} isLoading={isPending}>
              {needsAddress ? 'Send secure link' : 'Create link'}
            </ButtonNew>
          </Box>
        </FormikForm>
      )}
    </Dialog>
  );
};
